import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { fmtMoney, fmtMonth, fmtNumber } from '../lib/format';
import type { ProfitView } from '../lib/types';
import { Button, Callout, Empty, ErrorState, Input, Ltr, SkeletonRows } from '../components/ui';
import { IconDownload } from '../components/icons';
import { MarginBadge, profitTitle } from '../components/Margin';
import { ProfitGaps } from '../components/ProfitGaps';

type Group = 'customer' | 'product' | 'category' | 'month';
type Row = ProfitView & { key: string; label: string; sub: string | null; invoices: number; customers: number; prevMargin: number | null; toTarget: number };
type Report = {
  group: Group;
  from: string;
  to: string;
  prev: { from: string; to: string };
  target: number;
  warn: number;
  total: ProfitView & { invoices: number; customers: number };
  prevTotal: ProfitView & { invoices: number; customers: number };
  rows: Row[];
};
type Sort = 'profit' | 'margin' | 'revenue' | 'toTarget';

const GROUPS: Array<{ key: Group; label: string }> = [
  { key: 'customer', label: 'לקוחות' },
  { key: 'product', label: 'מוצרים' },
  { key: 'category', label: 'קטגוריות' },
  { key: 'month', label: 'חודשים' },
];

const SINGULAR: Record<Group, string> = { customer: 'לקוח', product: 'מוצר', category: 'קטגוריה', month: 'חודש' };

/** Change in margin points vs the previous period of the same length. */
function Trend({ now, before }: { now: number | null; before: number | null }) {
  if (now == null || before == null) return <span className="faint">—</span>;
  const d = Math.round((now - before) * 10) / 10;
  if (Math.abs(d) < 0.5) return <span className="muted small">ללא שינוי</span>;
  return (
    <span className="small" style={{ color: d > 0 ? 'var(--ok)' : 'var(--bad)' }} title={`בתקופה הקודמת ${before}%`}>
      <bdi dir="ltr">{d > 0 ? '▲' : '▼'} {Math.abs(d)}</bdi> נק׳
    </span>
  );
}

/** Realized gross margin by customer, product, category and month (profit permission only). */
export function ProfitPage() {
  const [params, setParams] = useSearchParams();
  const group = (params.get('group') as Group) || 'customer';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const [sort, setSort] = useState<Sort>('profit');
  const [q, setQ] = useState('');
  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    setParams(next, { replace: true });
  };
  const { data, error, refetch, isLoading } = useQuery({
    queryKey: ['profit', 'report', group, from, to],
    queryFn: () => api<Report>(`/api/profit/report${qs({ group, from: from || undefined, to: to || undefined })}`),
    placeholderData: keepPreviousData,
  });

  const rows = (data?.rows ?? [])
    .filter((r) => !q.trim() || `${r.label} ${r.sub ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()))
    .slice()
    .sort((a, b) =>
      group === 'month'
        ? b.key.localeCompare(a.key)
        : sort === 'margin'
          ? (a.margin ?? 999) - (b.margin ?? 999)
          : sort === 'revenue'
            ? b.revenue - a.revenue
            : sort === 'toTarget'
              ? b.toTarget - a.toTarget
              : b.profit - a.profit,
    );
  const label = (r: Row) => (group === 'month' ? fmtMonth(r.key) : r.label);
  const cols = group === 'month' ? 7 : 9;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>רווחיות</h1>
          <div className="sub">
            רווח גולמי מהחשבוניות שיצאו (החשבון הראשי בלבד), לפי העלות של כל מוצר בתאריך החשבונית. שורות בלי מוצר משויך או בלי עלות לא נכנסות לאחוז — ה"כיסוי" מראה כמה מהמכירות חושבו.
          </div>
        </div>
        <div className="row wrap">
        <Link to="/profit/substitutes" className="btn">קבוצות תחליפים</Link>
        <a className="btn" href={`/api/profit/report/export${qs({ group, from: from || undefined, to: to || undefined })}`}>
          <IconDownload /> Excel
        </a>
        </div>
      </div>

      <div className="stack">
        {data ? (
          <section className="kpis" aria-label="סיכום">
            <div className="kpi" title={profitTitle(data.total)}>
              <div className="kpi-label">רווחיות בתקופה</div>
              <div className="kpi-value"><MarginBadge margin={data.total.margin} level={data.total.level} title={profitTitle(data.total)} /></div>
              <div className="kpi-meta">
                <Trend now={data.total.margin} before={data.prevTotal.margin} /> מול התקופה הקודמת · יעד {data.target}%
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-label">רווח גולמי</div>
              <div className="kpi-value">{fmtMoney(data.total.profit, { round: true })}</div>
              <div className="kpi-meta">ממכירות של {fmtMoney(data.total.covered, { round: true })} עם עלות ידועה</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">כיסוי</div>
              <div className="kpi-value">{data.total.coverage}%</div>
              <div className="kpi-meta">
                מתוך {fmtMoney(data.total.revenue, { round: true })} · <Link to="/products/matching">שיוך שורות</Link> מעלה את הכיסוי
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-label">חשבוניות</div>
              <div className="kpi-value">{fmtNumber(data.total.invoices)}</div>
              <div className="kpi-meta">{fmtNumber(data.total.customers)} לקוחות</div>
            </div>
          </section>
        ) : null}

        {data && group === 'customer' ? (() => {
          const below = data.rows.filter((r) => r.toTarget > 0);
          const total = below.reduce((s, r) => s + r.toTarget, 0);
          return below.length ? (
            <Callout tone="info" action={sort !== 'toTarget' ? <Button size="sm" onClick={() => setSort('toTarget')}>לקוחות לטיפול</Button> : null}>
              {fmtNumber(below.length)} לקוחות מתחת ליעד — אילו היו ביעד, הרווח בתקופה היה גבוה בכ-{fmtMoney(total, { round: true })}. בכרטיס הלקוח ← "רווחיות" יש הצעות מוכנות והצעת מחיר מעודכנת בלחיצה.
            </Callout>
          ) : null;
        })() : null}

        <section className="panel">
          <div className="panel-head row wrap" style={{ gap: 10 }}>
            <div className="segmented" role="group" aria-label="קיבוץ">
              {GROUPS.map((g) => (
                <button key={g.key} className={group === g.key ? 'active' : ''} onClick={() => update({ group: g.key })}>
                  {g.label}
                </button>
              ))}
            </div>
            {group !== 'month' ? <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="חיפוש" style={{ maxWidth: 200 }} aria-label="חיפוש" /> : null}
            <label className="row small muted">
              מתאריך
              <Input type="date" value={from || data?.from || ''} onChange={(e) => update({ from: e.target.value || null })} style={{ width: 150 }} />
            </label>
            <label className="row small muted">
              עד
              <Input type="date" value={to || data?.to || ''} onChange={(e) => update({ to: e.target.value || null })} style={{ width: 150 }} />
            </label>
            {from || to ? <button className="btn ghost sm" onClick={() => update({ from: null, to: null })}>12 החודשים האחרונים</button> : null}
            <span className="spacer" />
            {group !== 'month' ? (
              <div className="segmented" role="group" aria-label="מיון">
                <button className={sort === 'profit' ? 'active' : ''} onClick={() => setSort('profit')} title="הכי הרבה רווח גולמי">חזקים</button>
                <button className={sort === 'margin' ? 'active' : ''} onClick={() => setSort('margin')} title="האחוז הנמוך ביותר קודם">חלשים</button>
                <button className={sort === 'toTarget' ? 'active' : ''} onClick={() => setSort('toTarget')} title="כמה רווח נוסף אם היו ביעד">פוטנציאל</button>
                <button className={sort === 'revenue' ? 'active' : ''} onClick={() => setSort('revenue')}>מכירות</button>
              </div>
            ) : null}
          </div>
          {error ? (
            <ErrorState error={error} onRetry={() => refetch()} />
          ) : (
            <div className="table-wrap" style={{ maxHeight: 'calc(100vh - 330px)', overflowY: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>{SINGULAR[group]}</th>
                    <th className="num">מכירות</th>
                    <th className="num">רווח גולמי</th>
                    <th className="num">רווחיות</th>
                    {group !== 'month' ? <th className="num hide-sm">מגמה</th> : null}
                    <th className="num hide-sm">כיסוי</th>
                    <th className="num hide-sm">חשבוניות</th>
                    {group !== 'month' ? <th className="num" title="רווח גולמי נוסף אילו המכירות היו ביעד הרווחיות">עד היעד</th> : null}
                    <th className="num hide-sm">עלות</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && !data ? (
                    <SkeletonRows rows={10} cols={cols} />
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={cols}><Empty title="אין חשבוניות בטווח" /></td></tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.key}>
                        <td className="primary">
                          {group === 'customer' ? (
                            <Link to={`/customers/${r.key}?tab=profit`} style={{ color: 'var(--ink)' }}>{r.label}</Link>
                          ) : group === 'month' ? (
                            <Link to={`/invoices?from=${r.key}-01&to=${r.key}-31`} style={{ color: 'var(--ink)' }}>{label(r)}</Link>
                          ) : (
                            label(r)
                          )}
                          {r.sub ? <span className="sub"><Ltr>{r.sub}</Ltr></span> : null}
                          {group === 'product' || group === 'category' ? <span className="sub">{fmtNumber(r.customers)} לקוחות</span> : null}
                        </td>
                        <td className="num tabular">{fmtMoney(r.revenue, { round: true })}</td>
                        <td className="num tabular strong">{r.margin == null ? <span className="faint">—</span> : fmtMoney(r.profit, { round: true })}</td>
                        <td className="num"><MarginBadge margin={r.margin} level={r.level} title={profitTitle(r)} /></td>
                        {group !== 'month' ? <td className="num hide-sm"><Trend now={r.margin} before={r.prevMargin} /></td> : null}
                        <td className="num tabular hide-sm" style={r.coverage < 60 ? { color: 'var(--warn)' } : undefined}>{r.coverage}%</td>
                        <td className="num tabular hide-sm">{fmtNumber(r.invoices)}</td>
                        {group !== 'month' ? <td className="num tabular">{r.toTarget ? fmtMoney(r.toTarget, { round: true }) : <span className="faint">—</span>}</td> : null}
                        <td className="num tabular hide-sm muted">{r.margin == null ? '—' : fmtMoney(r.cost, { round: true })}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <ProfitGaps />
      </div>
    </>
  );
}
